import { useEffect, useRef } from 'react'
import { GripHorizontal, X } from 'lucide-react'
import { Handle, Position, type NodeProps } from 'reactflow'
import { useTranslation } from '@/i18n'
import { LiveSeriesCard } from '@/components/live/LiveSeriesCard'

/** Size of a live card on the canvas; the layout (run/runGraph.ts) reserves the same room. */
export const LIVE_CARD_WIDTH = 520
export const LIVE_CARD_HEIGHT = 380

export interface LiveCardNodeData {
  documentId: string
  tags: string[]
  title: string
  onRemove: () => void
}

/**
 * A run's live card as a canvas node: a thin grip bar to drag it by and a remove button, over the
 * shared LiveSeriesCard. The body is `nodrag nowheel`, so its tag picker and buttons take clicks
 * and scrolling it does not pan the canvas. The node is the card's frame, so the shared card drops
 * its own border and rounded corners here.
 */
export default function LiveCardNode({ data }: NodeProps<LiveCardNodeData>) {
  const { t } = useTranslation()
  const body = useRef<HTMLDivElement>(null)
  // React Flow drops every wheel inside `nowheel`, a pinch too, and the browser then zooms the
  // page. A pinch or ctrl/⌘+scroll over the card goes to the canvas instead (FS-723).
  useEffect(() => {
    const el = body.current
    const onWheel = (e: WheelEvent) => {
      const pane = e.ctrlKey || e.metaKey ? el?.closest('.react-flow__renderer') : null
      if (!pane) return
      e.preventDefault()
      e.stopPropagation()
      pane.dispatchEvent(new WheelEvent('wheel', e))
    }
    el?.addEventListener('wheel', onWheel, { passive: false })
    return () => el?.removeEventListener('wheel', onWheel)
  }, [])
  return (
    <div
      style={{ width: LIVE_CARD_WIDTH, height: LIVE_CARD_HEIGHT }}
      className="flex flex-col overflow-hidden rounded-lg border border-border bg-background shadow-sm dark:bg-dark-card"
    >
      <Handle id="s-right" type="source" position={Position.Right} className="!opacity-0" />
      <div className="flex h-7 shrink-0 cursor-grab items-center justify-between border-b border-border/70 px-2 text-muted-foreground active:cursor-grabbing">
        <GripHorizontal className="size-4" aria-hidden />
        <button
          type="button"
          onClick={data.onRemove}
          aria-label={t('workflow:builder.live_card_remove', {
            defaultValue: 'Remove live card: {{title}}',
            title: data.title,
          })}
          className="nodrag grid size-5 place-items-center rounded-md hover:bg-muted hover:text-foreground dark:hover:bg-dark-muted"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <div ref={body} className="nodrag nowheel min-h-0 flex-1" onPointerDown={(e) => e.stopPropagation()}>
        <LiveSeriesCard documentId={data.documentId} tags={data.tags} title={data.title} className="h-full rounded-none border-0" />
      </div>
    </div>
  )
}
